import { Col } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { Button } from 'common/components/Button/Button'
import Show from 'common/components/Show2/Show'
import { DATE_FORMAT } from 'common/constants/dateFormatConstants'
import { useTimezone } from 'common/hooks/useTimezone'
import { DateService } from 'common/services/dateService'
import { DocumentResourceHeader } from 'features/Home/Book/Booking/Schedule/PatientSchedule/DocumentResourceHeader/DocumentResourceHeader'
import { EmptyRoomsList } from 'features/Home/Book/Booking/Schedule/PatientSchedule/EmptyRoomsList/EmptyRoomsList'
import { PatientScheduleSkeleton } from 'features/Home/Book/Booking/Schedule/PatientSchedule/PatientScheduleSkeleton/PatientScheduleSkeleton'
import { PatientSelection } from 'features/Home/Book/Booking/Schedule/PatientSchedule/PatientSelection/PatientSelection'
import { ScheduleCalendar } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/ScheduleCalendar'
import {
  ALLOW_RESCHEDULE_CURRENT_DAY_PAST_EVENTS,
  INFO_CONSTANTS,
  PATIENT_STATUSES,
} from 'features/Home/constants/infoConstants'
import type { IPatientRoom } from 'features/Home/interfaces/IInfoPatient'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import type { ScheduleMonth } from 'features/Schedule/api/schedule.types'
import { ManageAppointment } from 'features/Schedule/pages/components/ManageAppointment/ManageAppointment'
import { ManageEhrAppointment } from 'features/Schedule/pages/components/ManageAppointment/ManageEhrAppointment'
import { useScheduleActions } from 'features/Schedule/pages/components/ScheduleDayView/hooks/useScheduleActions'
import { useScheduleData } from 'features/Schedule/pages/components/ScheduleDayView/hooks/useScheduleData'
import { type EScheduleView } from 'features/Schedule/pages/Schedule'
import { useSchedulePusher } from 'features/Schedule/pusher/useSchedulePusher'
import { PathUtils } from 'routes/routes.utils'

const { preserveDateInTimezone } = DateService

interface ScheduleDayViewProps {
  defaultActiveDate: string | null
  selectedConsultationStaffId: string
  selectedRoomIds?: string[] | null
  scheduleView: EScheduleView
  scheduleMonthData?: ScheduleMonth
}

export const ScheduleDayView = ({
  defaultActiveDate,
  selectedConsultationStaffId,
  selectedRoomIds,
  scheduleView,
  scheduleMonthData,
}: ScheduleDayViewProps) => {
  const { timeZone } = useTimezone()

  const navigate = useNavigate()
  const { siteId } = useParams()

  const [editedPatient, setEditedPatient] = useState<IPatientSchedule | null>(null)
  const [addPatient, setAddPatient] = useState<boolean>(false)
  const [customAppointmentData, setCustomAppointmentData] = useState<{
    appointment: Date[]
    room: string
  } | null>(null)

  const today = useMemo(() => {
    return preserveDateInTimezone(dayjs().startOf('day'), timeZone).format(
      DATE_FORMAT.PATIENT_TRACKER_DATE,
    )
  }, [timeZone])

  const {
    scheduleDay,
    isFetchingDay,
    isLoadingDay,
    rooms,
    schedules,
    consultationStaffs,
    languages,
    timeInterval,
    disabledIntervals,
    eventsToSendNotification,
    doctorBufferTime,
    roomStatuses,
    defaultAppointmentTime,
    patientStatuses,
    appointmentCategories,
    hasEhrBooking,
    byPassTemperatureCheck,
    hasOccupied,
    appointmentColor,
    punctuality,
    patientBufferTime,
  } = useScheduleData(defaultActiveDate, selectedConsultationStaffId)

  const visibleRooms = useMemo(() => {
    if (!selectedRoomIds?.length) return rooms
    const filteredRooms = rooms.filter((room) => selectedRoomIds.includes(room.value))
    return filteredRooms.length > 0 ? filteredRooms : rooms
  }, [rooms, selectedRoomIds])

  const visibleDisabledIntervals = useMemo(() => {
    if (visibleRooms === rooms) return disabledIntervals
    const visibleResourceIds = new Set(
      visibleRooms.map((room) => `${room.label}/${room.value}`),
    )
    return disabledIntervals.filter((interval) => visibleResourceIds.has(interval.resourceId))
  }, [disabledIntervals, rooms, visibleRooms])

  useSchedulePusher(defaultActiveDate)

  useEffect(() => {
    if (!editedPatient?.id) return

    const latestPatient = schedules.find((schedule) => schedule.id === editedPatient.id)
    if (!latestPatient || latestPatient === editedPatient) return

    const hasEncounterChanged =
      latestPatient.emr_encounter_id !== editedPatient.emr_encounter_id ||
      latestPatient.emr_encounter_uuid !== editedPatient.emr_encounter_uuid ||
      latestPatient.emr_updated_at !== editedPatient.emr_updated_at

    if (hasEncounterChanged) {
      setEditedPatient(latestPatient)
    }
  }, [schedules, editedPatient])

  const {
    updateAppointment,
    updateRoomStatus,
    notifyMultiplePatients,
    isNotifying,
    isUpdatingRoomStatus,
    isNotifyingMultiple,
  } = useScheduleActions()

  const isLoading =
    !defaultActiveDate || isLoadingDay || (isFetchingDay && !scheduleDay)
  const hasNoBooking = !isLoading && (!timeInterval?.max || !timeInterval?.min || !rooms?.length)

  const getWaitingRoomPatients = () => {
    try {
      const waitingRoom = schedules.find((room: IPatientSchedule) => {
        return room.room.is_waiting_room
      })
      if (waitingRoom) {
        const waitingRoomCode = waitingRoom.room.value
        const checkedPatientsInWaitingRoom = schedules.filter((patient: IPatientSchedule) => {
          return (
            patient.room.value === waitingRoomCode &&
            patient.status.code === PATIENT_STATUSES.CHECKED_IN_ON_SITE
          )
        })
        return checkedPatientsInWaitingRoom.length
      }
      return null
    } catch (error) {
      return null
    }
  }

  const updateStatus = (value: number, room: IPatientRoom) => {
    updateRoomStatus(
      room,
      {
        status_type_code: value,
        lease_id: room?.booking_id,
      },
      defaultActiveDate,
    )
  }

  const handleCloseModal = () => {
    setAddPatient(false)
    setEditedPatient(null)
    setCustomAppointmentData(null)
  }

  const createScheduleWithCustomAppointment = (
    start: Date,
    end: Date,
    resourceId: string,
  ): void => {
    setCustomAppointmentData({ appointment: [start, end], room: resourceId })
    setAddPatient(true)
  }

  const roomsHeader = useMemo(() => {
    if (!defaultActiveDate || !timeZone) return undefined
    const isSameDate = preserveDateInTimezone(defaultActiveDate, timeZone).isSame(
      preserveDateInTimezone(today, timeZone),
      'day',
    )

    const sortedRooms = [...visibleRooms].sort((a, b) => {
      if (a.is_waiting_room && !b.is_waiting_room) return -1
      if (!a.is_waiting_room && b.is_waiting_room) return 1
      return (a.order ?? 0) - (b.order ?? 0)
    })

    return sortedRooms.map((room: IPatientRoom) => {
      return {
        resourceId: `${room.label}/${room.value}`,
        resourceTemplate: (
          <DocumentResourceHeader
            hasOccupied={hasOccupied}
            doctorsBufferTime={doctorBufferTime}
            room={room}
            hasRoomStatus={!room.is_waiting_room && !!room.add_patient && isSameDate}
            statusList={roomStatuses}
            updateStatus={updateStatus}
            timeZone={timeZone}
            key={room.id}
            disabledIntervals={visibleDisabledIntervals}
            dayInterval={timeInterval}
            isUpdatingRoomStatus={isUpdatingRoomStatus}
            {...(room.is_waiting_room && { patientsCount: getWaitingRoomPatients() })}
          />
        ),
      }
    })
  }, [
    JSON.stringify(visibleRooms),
    JSON.stringify(visibleDisabledIntervals),
    JSON.stringify(roomStatuses),
    JSON.stringify(doctorBufferTime),
    JSON.stringify(timeInterval),
    timeZone,
    isUpdatingRoomStatus,
    defaultActiveDate,
  ])

  const disabledInputsManageAppointment = useMemo(() => {
    if (!editedPatient) return false
    return !ALLOW_RESCHEDULE_CURRENT_DAY_PAST_EVENTS.includes(editedPatient?.status.code)
  }, [editedPatient])

  const handleUpdateEvents = useCallback(
    async (adjustedData: any, event: any) => {
      return await updateAppointment(adjustedData, defaultActiveDate, event.id)
    },
    [defaultActiveDate],
  )

  const manageAppointmentTitle = useMemo(() => {
    if (editedPatient) {
      return INFO_CONSTANTS.EDIT_APPOINTMENT
    }
    return INFO_CONSTANTS.ADD_APPOINTMENT
  }, [editedPatient, defaultActiveDate])

  return (
    <Col xs={24}>
      <Show>
        <Show.When isVisible={isLoading}>
          <PatientScheduleSkeleton />
        </Show.When>
        <Show.When isVisible={hasNoBooking}>
          <EmptyRoomsList
            title={INFO_CONSTANTS.NO_ACTIVE_SCHEDULE_TODAY}
            content={
              <Button onClick={() => navigate(PathUtils.getBook(siteId))}>Book now →</Button>
            }
          />
        </Show.When>
        <Show.Else>
          <ScheduleCalendar
            appointmentColor={appointmentColor}
            sendInfoPatientsNotification={notifyMultiplePatients}
            setEditedPatient={setEditedPatient}
            createCustomAppointment={createScheduleWithCustomAppointment}
            handleUpdateEvents={handleUpdateEvents}
            showConsultationStaffInitials={selectedConsultationStaffId === '-1'}
            resources={roomsHeader}
            prepopulateTimeOnClick={defaultAppointmentTime}
            dayInterval={timeInterval}
            eventsToSendNotification={eventsToSendNotification}
            isSendingNotifications={isNotifying || isNotifyingMultiple}
            disabledIntervals={visibleDisabledIntervals}
            timeZone={timeZone}
            rooms={visibleRooms}
            schedules={schedules}
            patientStatuses={patientStatuses}
            appointmentCategories={appointmentCategories}
            hasEhrBooking={hasEhrBooking}
          />
        </Show.Else>
      </Show>

      <Show>
        <Show.When isVisible={hasEhrBooking}>
          <ManageEhrAppointment
            scheduleView={scheduleView}
            patientStatuses={patientStatuses}
            title={manageAppointmentTitle}
            date={defaultActiveDate}
            open={addPatient || !!editedPatient}
            isEditing={!!editedPatient}
            editedPatientData={editedPatient}
            setOpen={handleCloseModal}
            consultation_staffs={consultationStaffs}
            dayInterval={timeInterval}
            languages={languages}
            prePopulateSchedule={defaultAppointmentTime}
            disabledEverything={disabledInputsManageAppointment}
            customAppointment={customAppointmentData}
            rooms={rooms}
            schedules={schedules}
            timeInterval={timeInterval}
            disabledIntervals={disabledIntervals}
            timeZone={timeZone}
            appointmentCategories={appointmentCategories}
            isSendingNotifications={false}
            byPassTemperatureCheck={byPassTemperatureCheck}
            punctuality={punctuality}
            patientBufferTime={patientBufferTime}
            scheduleMonthData={scheduleMonthData}
          />
        </Show.When>
        <Show.Else>
          <ManageAppointment
            scheduleView={scheduleView}
            patientStatuses={patientStatuses}
            title={manageAppointmentTitle}
            date={defaultActiveDate}
            open={addPatient || !!editedPatient}
            isEditing={!!editedPatient}
            editedPatientData={editedPatient}
            setOpen={handleCloseModal}
            consultation_staffs={consultationStaffs}
            dayInterval={timeInterval}
            languages={languages}
            prePopulateSchedule={defaultAppointmentTime}
            disabledEverything={disabledInputsManageAppointment}
            customAppointment={customAppointmentData}
            rooms={rooms}
            schedules={schedules}
            timeInterval={timeInterval}
            disabledIntervals={disabledIntervals}
            timeZone={timeZone}
            appointmentCategories={appointmentCategories}
            isSendingNotifications={false}
            byPassTemperatureCheck={byPassTemperatureCheck}
          />
        </Show.Else>
      </Show>

      <PatientSelection />
    </Col>
  )
}
